<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const contactPanelTitle = 'Dados do contato';
const CONTACT_PANEL_MAX_WIDTH = 990;
const isClosing = ref(false);
const isCompactPanelLayout = ref(false);
const panelBounds = ref(null);
let closeTimer;
let panelResizeObserver;

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const modalOverlayStyle = computed(() => {
  if (isCompactPanelLayout.value || !panelBounds.value) {
    return {
      '--contact-panel-left': '0px',
      '--contact-panel-right': '0px',
    };
  }

  const { left, right } = panelBounds.value;
  return {
    '--contact-panel-left': `${left}px`,
    '--contact-panel-right': `${right}px`,
  };
});

const modalContainerStyle = computed(() => {
  if (isCompactPanelLayout.value || !panelBounds.value) {
    return { width: '100%' };
  }

  return {
    width: `${Math.round(
      Math.min(CONTACT_PANEL_MAX_WIDTH, panelBounds.value.width)
    )}px`,
  };
});

const updatePanelBounds = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const conversationWrap = document.querySelector('.conversation-details-wrap');

  if (!conversationWrap) {
    panelBounds.value = null;
    return;
  }

  const bounds = conversationWrap.getBoundingClientRect();
  const isNarrowViewport =
    window.matchMedia?.('(max-width: 767px)').matches ?? false;
  const isTouchViewport =
    window.matchMedia?.('(pointer: coarse) and (max-width: 1024px)').matches ??
    false;
  isCompactPanelLayout.value = isNarrowViewport || isTouchViewport;

  panelBounds.value = {
    left: Math.max(0, bounds.left),
    right: Math.max(0, window.innerWidth - bounds.right),
    width: bounds.width,
  };
};

const closeContactPanel = () => {
  if (
    !uiSettings.value?.is_contact_sidebar_open &&
    !uiSettings.value?.is_copilot_panel_open
  ) {
    return;
  }

  isClosing.value = true;
  window.clearTimeout(closeTimer);
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const handleContactPanelOutsideClick = () => {
  closeContactPanel();
};

watch(
  () => props.currentChat?.id,
  (conversationId, previousConversationId) => {
    if (conversationId && conversationId !== previousConversationId) {
      closeContactPanel();
    }
  }
);

watch(activeTab, async value => {
  if (value === 0) {
    isClosing.value = false;
    await nextTick();
    updatePanelBounds();
  }
});

onMounted(async () => {
  await nextTick();
  updatePanelBounds();

  const conversationWrap = document.querySelector('.conversation-details-wrap');
  if (conversationWrap && typeof ResizeObserver !== 'undefined') {
    panelResizeObserver = new ResizeObserver(updatePanelBounds);
    panelResizeObserver.observe(conversationWrap);
  }

  window.addEventListener('resize', updatePanelBounds);
});

onBeforeUnmount(() => {
  window.clearTimeout(closeTimer);
  panelResizeObserver?.disconnect();
  window.removeEventListener('resize', updatePanelBounds);
});
</script>

<template>
  <Teleport to="body" :disabled="!isCompactPanelLayout">
    <div
      class="contact-details-modal-overlay pointer-events-none fixed bottom-0 top-0 z-[2147483647] flex min-w-0 justify-end overflow-x-hidden overflow-y-hidden"
      :class="{
        'contact-details-modal-overlay--compact': isCompactPanelLayout,
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      }"
      :style="modalOverlayStyle"
    >
      <div
        v-on-click-outside="[
          handleContactPanelOutsideClick,
          {
            // A galeria e o modal de reenvio nascem fora do painel (teleport),
            // e fechar o painel desmonta a ConversationSidebar via v-if --
            // levando a galeria junto no meio do clique.
            ignore: [
              '.contact-edit-drawer',
              '.contact-details-media-modal',
              '.gallery-viewer-overlay',
              '.acl-contact-pdf-viewer',
              '.acl-forward-overlay',
            ],
          },
        ]"
        class="contact-details-modal-container pointer-events-auto relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-n-weak bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]"
        :class="{
          'contact-details-modal-container--closing': isClosing,
          'contact-details-modal-container--compact': isCompactPanelLayout,
        }"
        :style="modalContainerStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="contactPanelTitle"
      >
        <ContactPanel
          v-show="activeTab === 0"
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.contact-details-modal-overlay {
  left: var(--contact-panel-left, 0px);
  right: var(--contact-panel-right, 0px);
  z-index: 2147483647 !important;
}

.contact-details-modal-overlay--compact {
  inset: 0 !important;
  width: auto !important;
  max-width: none;
}

.contact-details-modal-container {
  box-sizing: border-box;
  max-width: 100%;
  animation: contact-details-panel-enter 220ms ease-out;
  border-radius: 0;
  transition: transform 220ms ease-in-out;
}

// The Aceleres stylesheet historically turns this fixed overlay into a flex
// sibling on desktop, which shrinks the conversation. Keep the panel on its
// own layer and let the chat remain behind it.
@media (min-width: 1440px) {
  :global(
      .painel-atendimento
        .contact-details-modal-overlay.contact-details-modal-overlay
    ) {
    position: fixed !important;
    top: 0 !important;
    right: var(--contact-panel-right, 0px) !important;
    bottom: 0 !important;
    left: var(--contact-panel-left, 0px) !important;
    width: auto !important;
    height: auto !important;
    flex: none !important;
    align-self: auto !important;
  }

  :global(
      .painel-atendimento
        .contact-details-modal-container.contact-details-modal-container--compact
    ) {
    width: 100% !important;
  }
}

.contact-details-modal-container--compact {
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  border-right: 0;
  border-left: 0;
  border-radius: 0;
}

.contact-details-modal-container--closing {
  transform: translateX(100%);
}

@keyframes contact-details-panel-enter {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}
</style>
